import { ComponentPropsWithoutRef, FC } from 'react';
import Markdown from 'shared/components/Markdown';
import { arrowStyles } from 'shared/styles/arrow';
import { FONT_SIZES, SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import { materialFieldSSTStyles, materialLabelStyles } from 'inquiry/mixins/form';
import { colorCss } from 'lib/inquiryStyles';
import FormControlProps from 'lib/interfaces/FormControlProps';
import { InputSelectComponent } from 'lib/interfaces/ui-components';

type SelectProps = ComponentPropsWithoutRef<'select'> &
  FormControlProps & {
    styles: InputSelectComponent['styles'] | undefined;
  };

const Select = styled('select')<
  SelectProps & {
    focused: boolean | undefined;
    hasError: boolean | undefined;
    hasValue: boolean | undefined;
    hasWarning: boolean | undefined;
  }
>`
  /* Hide default styling */
  appearance: none;
  &::-ms-expand {
    display: none;
  }

  ${materialFieldSSTStyles}

  /* Select specific overrides */
  padding: ${SPACINGS.base}px 0px ${SPACINGS.base}px ${SPACINGS.base / 2}px;
  position: relative;
  color: ${({ hasValue, styles, theme }) => {
    // We need this complex logic because we cannot use ::placeholder for HTML selects.
    // This is intended to replicate the logic in placeholderSSTStyles().
    // This is simpler than the non-material version since we use less colors for different states.
    if (hasValue) return;
    return colorCss(styles?.['border-color']?.base?.base) ?? theme.colorInputBorder;
  }};
`;

const Wrapper = styled('div')`
  position: relative;
  width: 100%;

  &::after {
    ${arrowStyles};
    right: ${SPACINGS.base}px;
  }
`;

const StyledMarkdown = styled(Markdown)<
  SelectProps & {
    focused: boolean | undefined;
    hasError: boolean | undefined;
    hasValue: boolean | undefined;
    hasWarning: boolean | undefined;
  }
>`
  ${materialLabelStyles}

  /* select label is always in small / active mode */
  top: -14px;
  font-size: ${FONT_SIZES.small};
`;

interface MaterialSelectProps {
  labelText: string;
  focused?: boolean;
}

const MaterialSelect: FC<MaterialSelectProps & SelectProps> = ({
  className,
  focused,
  hasError,
  hasWarning,
  labelText,
  styles,
  ...passthroughProps
}) => {
  const hasValue = Boolean(passthroughProps.value);

  return (
    <Wrapper className={className}>
      <Select
        {...passthroughProps}
        focused={focused}
        hasError={hasError}
        hasValue={hasValue}
        hasWarning={hasWarning}
        styles={styles}
      />
      <StyledMarkdown
        wrapper='label'
        wrapperProps={{ htmlFor: passthroughProps.id }}
        focused={focused}
        hasError={hasError}
        hasValue={hasValue}
        hasWarning={hasWarning}
        styles={styles}
      >
        {labelText}
      </StyledMarkdown>
    </Wrapper>
  );
};
export default MaterialSelect;
